import { CURRENT_USER_QUERY } from "@lib/graphql/queries";
import type { Organisation } from "@lib/types";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useQuery } from "urql";

interface CurrentUserShellData {
  currentUser: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    superuser: boolean;
  } | null;
  currentOrganisation: { id: string; name: string; slug: string } | null;
  currentMembership: {
    role: string;
    superuserSynthetic: boolean;
    organisation: { id: string; name: string } | null;
  } | null;
  organisations: Organisation[];
}

interface CurrentUserContextValue extends CurrentUserShellData {
  fetching: boolean;
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [{ data, fetching }] = useQuery({ query: CURRENT_USER_QUERY });

  const value = useMemo<CurrentUserContextValue>(
    () => ({
      currentUser: data?.currentUser ?? null,
      currentOrganisation: data?.currentOrganisation ?? null,
      currentMembership: data?.currentMembership ?? null,
      organisations: data?.organisations ?? [],
      fetching,
    }),
    [data, fetching],
  );

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUserShell(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) {
    throw new Error("useCurrentUserShell must be used inside <CurrentUserProvider>");
  }
  return ctx;
}
